import {
  faChevronLeft,
  faEllipsisVertical,
  faMessage,
  faTimes,
  faVideo
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useContext, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import Debug from '@/components/Debug';
import DrugScreenCard from '@/components/DrugScreenCard';
import NestedDataList from '@/components/NestedDataList';
import PageHeader from '@/components/PageHeader';
import { Color } from '@/constants/color';
import defaultTimeZone from '@/constants/defaultTimeZone';
import { drugScreensDefaultSearchParams } from '@/constants/drugScreen';
import AppContext from '@/contexts/appContext';
import { useAppointment, useAppointmentAttendances } from '@/hooks/useAppointments';
import { useDrugScreens } from '@/hooks/useDrugScreens';
import useNavigateToPage from '@/hooks/useNavigateToPage';
import { useWebJoinLink } from '@/hooks/useWebJoinLink';
import VideoSessionsChart from '@/pages/AppointmentView/VideoSessionsChart';
import BulkMessageModal from '@/pages/MyPeople/components/BulkMessageModal/BulkMessageModal';
import PersonContext, { PersonContextInterface } from '@/pages/Person/PersonContext';
import { ScheduledBySource } from '@/types/appointments';
import { ENCOUNTER_TYPES } from '@/types/encounter';
import { Alert } from '@/ui';
import Accordion from '@/ui/components/Accordion';
import AppointmentAttendeeCard from '@/ui/components/AppointmentAttendeeCard';
import AuditLogLink from '@/ui/components/AuditLogLink';
import Badge from '@/ui/components/Badge';
import Button from '@/ui/components/Button';
import Card from '@/ui/components/Card';
import Container from '@/ui/components/Container';
import CopyContent from '@/ui/components/CopyContent';
import DataList, { DataRow } from '@/ui/components/DataList';
import Dropdown from '@/ui/components/Dropdown';
import DualTime from '@/ui/components/DualTime';
import Heading from '@/ui/components/Heading';
import Spinner from '@/ui/components/Spinner';
import formatStaffName from '@/utils/formatStaffName';
import { healthieAppointmentLink } from '@/utils/healthieLinks';
import { homebaseBaseUrl } from '@/utils/homebaseLink';
import snakeCaseToTitleCase from '@/utils/snakeToTitleCase';
import {
  GENERATE_APPOINTMENT_WEB_LINK,
  hasPermission,
  PEOPLE_VIEW_ENCOUNTERS
} from '@/utils/userPermission';

import AppointmentSummary from './AppointmentSummary';

const AppointmentView = () => {
  const { permissions, host } = useContext(AppContext);
  const navigate = useNavigate();

  const enableEncounters = useMemo((): boolean => {
    const requiredPermission = PEOPLE_VIEW_ENCOUNTERS;
    return hasPermission({ permissions, requiredPermission });
  }, [permissions]);

  const canGenerateJoinLink = hasPermission({
    permissions,
    requiredPermission: GENERATE_APPOINTMENT_WEB_LINK
  });

  // PersonContext is only present when rendered inside the /people/:personId/* tree.
  // Cast to Partial so we can safely handle the standalone /appointments/:appointmentId route.
  const { person } = useContext(PersonContext) as Partial<PersonContextInterface>;
  const personId: string | undefined = person?.id;
  const time_zone: string | undefined = person?.time_zone ?? undefined;

  const { appointmentId } = useParams();
  const { navigateToAppointments, navigateToProfileWithId, navigateToEncounterWithoutPerson } =
    useNavigateToPage();

  const { data: appointment, isPending: isLoadingAppointment } = useAppointment(
    appointmentId as string
  );

  const { data: drugScreensResponse, isPending: isLoadingDrugScreens } = useDrugScreens({
    ...drugScreensDefaultSearchParams,
    appointment_id: appointmentId
  });

  // Fetch ALL attendances for this appointment — no person_id filter — so every
  // attendee is always visible regardless of the navigation context.
  // Pass size=100 (the backend max_page_size) to avoid the default page_size=10
  // limit silently truncating group session members in BulkMessageModal.
  const { data: appointmentAttendancesResponse, isPending: isLoadingAppointmentAttendances } =
    useAppointmentAttendances({
      appointment_id: appointmentId,
      size: 100
    });

  const videoSessions = appointment?.video_sessions?.length ? appointment.video_sessions : null;

  const attendanceResults = useMemo(
    () => (appointmentAttendancesResponse?.data?.results ?? []).filter((a) => a.person != null),
    [appointmentAttendancesResponse]
  );

  const [attendanceFilterIds, setAttendanceFilterIds] = useState<Set<string> | null>(null);
  const [activeSummaryCard, setActiveSummaryCard] = useState<string | null>(null);
  const [isBulkModalOpen, setIsBulkModalOpen] = useState(false);

  const filteredAttendances = useMemo(
    () =>
      attendanceFilterIds
        ? attendanceResults.filter((a) => attendanceFilterIds.has(a.id))
        : attendanceResults,
    [attendanceResults, attendanceFilterIds]
  );

  // Join link in the General Info card only applies when focused on a single person.
  const focusedAttendance = personId
    ? attendanceResults.find((a) => a.person?.id === personId)
    : undefined;
  const attendanceId = focusedAttendance?.id;

  // The page-level cancellation alert shows the relevant attendee's own reason:
  // the focused person's, when viewing this appointment from their profile
  // (even for a group appointment), otherwise the sole attendee's for a
  // genuinely 1:1 appointment. With no focused person and multiple attendees,
  // each participant's own reason is still surfaced via their row's pill tooltip.
  const relevantAttendance =
    focusedAttendance ?? (attendanceResults.length === 1 ? attendanceResults[0] : null);
  const relevantCancellationReason = relevantAttendance?.cancellation_reason;
  const showCancellationAlert =
    relevantAttendance &&
    ['declined', 'cancelled'].includes(relevantAttendance.internal_status) &&
    relevantCancellationReason;

  // A group appointment's series is created by the group tooling, so who
  // scheduled the member is whoever added them to the group.
  const isGroupManaged =
    appointment?.scheduled_by_source === ScheduledBySource.GROUP_APPOINTMENT_MANAGEMENT;
  const groupEnrollment = relevantAttendance?.group_enrollment;

  const { joinLink, isLoading: loadingJoinLink, copiedJoinLink, fetchJoinLink } = useWebJoinLink();

  const headingText = useMemo(() => {
    if (appointment) {
      const { start_at, title } = appointment;
      const startAt = start_at ? (
        <>
          @{' '}
          <DualTime
            dateTime={new Date(start_at)}
            memberTZ={time_zone ?? defaultTimeZone}
            showWeekday
          />
        </>
      ) : null;

      return (
        <>
          {title} {startAt}
        </>
      );
    }

    return '';
  }, [appointment, time_zone]);

  const scheduledAt = useMemo(() => {
    if (appointment) {
      const { created_at, initiator } = appointment;

      if (initiator) {
        return (
          <DualTime
            dateTime={new Date(created_at)}
            memberTZ={initiator.timezone}
            tooltipText="Initiator Time"
          />
        );
      }

      return <DualTime dateTime={new Date(created_at)} memberTZ={time_zone ?? defaultTimeZone} />;
    }

    return 'Not Available';
  }, [appointment, time_zone]);

  const renderDrugScreens = useMemo(() => {
    if (isLoadingDrugScreens) {
      return (
        <div>
          <Spinner label="Loading Drug Screen data..." />
        </div>
      );
    }

    if (!drugScreensResponse) {
      return (
        <Alert variant="error" dismissable>
          Failed to load Drug Screen data
        </Alert>
      );
    }

    if (drugScreensResponse) {
      if (drugScreensResponse.data?.results.length === 0) {
        return null;
      }

      return (
        <section className="my-4">
          <Heading className="mb-1" value={3} text="Drug Screens Conducted In This Appointment" />
          {drugScreensResponse.data?.results.map((drugScreen) => {
            return (
              <DrugScreenCard
                key={drugScreen.id}
                drugScreen={drugScreen}
                personTimeZone={drugScreen.person.time_zone || defaultTimeZone}
              />
            );
          })}
        </section>
      );
    }

    return null;
  }, [drugScreensResponse, isLoadingDrugScreens]);

  const renderAttendances = useMemo(() => {
    if (isLoadingAppointmentAttendances) {
      return (
        <div>
          <Spinner label="Loading Appointment Attendance data..." />
        </div>
      );
    }

    if (!appointmentAttendancesResponse) {
      return (
        <Alert variant="error" dismissable>
          Failed to load Appointment Attendance data
        </Alert>
      );
    }
    if (appointmentAttendancesResponse) {
      if (attendanceResults.length === 0) {
        return null;
      }

      return (
        <>
          <section className="my-4">
            <div className="flex items-center gap-2 mb-1">
              <Heading
                value={3}
                text={
                  attendanceFilterIds
                    ? `Participants (${filteredAttendances.length} of ${attendanceResults.length})`
                    : `Participants (${attendanceResults.length})`
                }
              />
              <div className="ml-auto flex items-center gap-2">
                {attendanceFilterIds && (
                  <Button
                    size="xs"
                    onClick={() => {
                      setAttendanceFilterIds(null);
                      setActiveSummaryCard(null);
                    }}>
                    <FontAwesomeIcon icon={faTimes} className="mr-1" />
                    Clear filter
                  </Button>
                )}
                {filteredAttendances.length > 0 && (
                  <Button size="xs" color={Color.GREEN} onClick={() => setIsBulkModalOpen(true)}>
                    <FontAwesomeIcon icon={faMessage} className="mr-1" />
                    Send Message ({filteredAttendances.length})
                  </Button>
                )}
              </div>
            </div>
            <ul
              role="list"
              className="divide-y divide-gray-200 overflow-hidden rounded-xl border border-gray-200 bg-white">
              {[...filteredAttendances]
                .sort((a, b) => {
                  const aScore = a.person?.id === personId ? 0 : 1;
                  const bScore = b.person?.id === personId ? 0 : 1;
                  return aScore - bScore;
                })
                .map((appointmentAttendance) => {
                  const { person } = appointmentAttendance;
                  if (!person) return null;

                  return (
                    <AppointmentAttendeeCard
                      key={appointmentAttendance.id}
                      attendanceId={appointmentAttendance.id}
                      appointmentWithinAvailability={
                        appointmentAttendance.appointment_within_availability
                      }
                      person={person}
                      internalStatus={appointmentAttendance.internal_status}
                      cancellationReason={appointmentAttendance.cancellation_reason}
                      chartingCompleted={appointmentAttendance.charting_completed}
                      chartingStatus={appointmentAttendance.charting_status}
                      appointment={appointmentAttendance.appointment}
                      groupEnrollment={appointmentAttendance.group_enrollment}
                      rescheduledBySource={appointmentAttendance.appointment.rescheduled_by_source}
                      feedback={appointmentAttendance.feedback}
                      isFocused={
                        personId != null && person.id === personId && attendanceResults.length > 1
                      }
                      showJoinLink={canGenerateJoinLink && !personId}
                      enableEncounters={enableEncounters}
                      onViewProfile={navigateToProfileWithId}
                      onViewEncounter={(attendanceId) =>
                        navigateToEncounterWithoutPerson(
                          ENCOUNTER_TYPES.APPOINTMENT_ATTENDANCE,
                          attendanceId
                        )
                      }
                    />
                  );
                })}
            </ul>
          </section>

          {attendanceResults.length > 0 && attendanceResults[0]._appointment_type_config && (
            <section className="my-4">
              <Accordion buttonText="Appointment Type Config">
                <NestedDataList
                  convertToHtmlList={['description']}
                  data={attendanceResults[0]._appointment_type_config}
                />
              </Accordion>
            </section>
          )}
        </>
      );
    }

    return null;
  }, [
    appointmentAttendancesResponse,
    attendanceResults,
    filteredAttendances,
    attendanceFilterIds,
    isLoadingAppointmentAttendances,
    navigateToProfileWithId,
    navigateToEncounterWithoutPerson,
    enableEncounters,
    canGenerateJoinLink,
    personId,
    setIsBulkModalOpen
  ]);

  if (!appointmentId)
    return (
      <Alert variant="error" dismissable>
        No Appointment ID provided
      </Alert>
    );

  const handleBack = () => {
    if (personId) {
      navigateToAppointments(personId);
    } else {
      navigate(-1);
    }
  };

  return (
    <Container type="page">
      <div className="inline-flex items-center">
        <button className="w-6 inline-block text-center mb-1" onClick={handleBack} type="button">
          <FontAwesomeIcon icon={faChevronLeft} className="mr-1 text-xl" />
        </button>
        <PageHeader headingText={headingText} />
      </div>

      {isLoadingAppointment ? (
        <div>
          <Spinner />
        </div>
      ) : appointment ? (
        <div>
          <p>
            {appointment.providers && (
              <>
                with{' '}
                {appointment.providers
                  .map((provider) => formatStaffName({ staff: provider }))
                  .join(', ')}
              </>
            )}

            {appointment.duration_in_minutes && (
              <>, for {appointment.duration_in_minutes} minutes</>
            )}
            {appointment.meeting_status && (
              <span className="ml-2">
                <Badge color={Color.GRAY} text={appointment.meeting_status} />
              </span>
            )}
          </p>

          <div className="my-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              {appointment.healthie_id && appointment.start_at && (
                <a
                  className="text-blue-600 underline"
                  href={healthieAppointmentLink(
                    appointment.healthie_id,
                    appointment.start_at,
                    host
                  )}
                  target="_blank"
                  rel="noreferrer">
                  Open in Healthie
                </a>
              )}

              <a
                className="text-blue-600 underline"
                href={`${homebaseBaseUrl(host)}/appointments/${appointmentId}`}
                target="_blank"
                rel="noreferrer">
                Open in Homebase
              </a>

              <Dropdown
                buttonText={<FontAwesomeIcon icon={faEllipsisVertical} />}
                showChevronIcon={false}
                customButtonClassName="p-1 text-gray-400 hover:text-gray-600"
                items={[
                  <AuditLogLink
                    key="audit"
                    entityType="appointment"
                    entityId={appointmentId}
                    className="px-3 py-2 text-sm text-gray-700">
                    Audit Log
                  </AuditLogLink>,
                  <Link
                    key="encounter"
                    to={`/encounters/appointment/by_healthie_id/${appointment.healthie_id}/`}>
                    View Encounter
                  </Link>
                ]}
              />
            </div>

            <Button
              size="xs"
              color={Color.BLUE}
              onClick={() => navigate(`/appointments/${appointmentId}/meeting`)}>
              <FontAwesomeIcon icon={faVideo} className="mr-1" />
              Start Meeting
            </Button>
          </div>

          {showCancellationAlert && relevantCancellationReason && (
            <Alert variant="error">
              <strong>{snakeCaseToTitleCase(relevantAttendance.internal_status)}</strong>
              <div>
                Reason: {relevantCancellationReason.name}
                {relevantCancellationReason.comment
                  ? ` — ${relevantCancellationReason.comment}`
                  : ''}
              </div>
            </Alert>
          )}

          <section className="my-4">
            <Accordion buttonText="General Info">
              <DataList>
                <DataRow label="Host URL">
                  {appointment.external_links?.zoom_start?.url ? (
                    <CopyContent
                      content={appointment.external_links.zoom_start.url}
                      wrapperClassName="max-w-full">
                      <a
                        className="block truncate text-blue-600 underline"
                        href={appointment.external_links.zoom_start.url}
                        title={appointment.external_links.zoom_start.url}
                        target="_blank"
                        rel="noreferrer">
                        {appointment.external_links.zoom_start.url}
                      </a>
                    </CopyContent>
                  ) : (
                    'Not Available'
                  )}
                </DataRow>

                {appointment.recurring_appointment && (
                  <DataRow label="Recurring Appointment">
                    <DataList>
                      <DataRow label="Repeat Interval">
                        {appointment.recurring_appointment.repeat_interval}
                      </DataRow>

                      <DataRow label="Repeat Times">
                        {appointment.recurring_appointment.repeat_times}
                      </DataRow>
                    </DataList>
                  </DataRow>
                )}

                {isGroupManaged ? (
                  <>
                    <DataRow label="Added to Group">
                      {relevantAttendance ? (
                        <DataList>
                          <DataRow label="By">
                            {groupEnrollment?.enrolled_by
                              ? formatStaffName({ staff: groupEnrollment.enrolled_by })
                              : 'Not Available'}
                          </DataRow>

                          <DataRow label="At">
                            {groupEnrollment?.enrolled_at ? (
                              <DualTime
                                dateTime={new Date(groupEnrollment.enrolled_at)}
                                memberTZ={time_zone ?? defaultTimeZone}
                              />
                            ) : (
                              'Not Available'
                            )}
                          </DataRow>
                        </DataList>
                      ) : (
                        'Shown on each attendee'
                      )}
                    </DataRow>

                    <DataRow label="Group Session Created">{scheduledAt}</DataRow>
                  </>
                ) : (
                  <DataRow label="Scheduled">
                    <DataList>
                      <DataRow label="By">
                        {appointment.scheduled_by
                          ? formatStaffName({ staff: appointment.scheduled_by })
                          : 'Not Available'}
                      </DataRow>

                      <DataRow label="At">{scheduledAt}</DataRow>
                    </DataList>
                  </DataRow>
                )}

                {appointment.rescheduled_by && appointment.meeting_status === 'rescheduled' && (
                  <DataRow label="Rescheduled">
                    <DataList>
                      <DataRow label="By">
                        {formatStaffName({ staff: appointment.rescheduled_by })}
                      </DataRow>

                      <DataRow label="At">
                        {appointment.last_rescheduled_at ? (
                          <DualTime
                            dateTime={new Date(appointment.last_rescheduled_at)}
                            memberTZ={time_zone ?? defaultTimeZone}
                          />
                        ) : (
                          'Not Available'
                        )}
                      </DataRow>
                    </DataList>
                  </DataRow>
                )}

                <DataRow label="Healthie ID">
                  {appointment.healthie_id ? (
                    <CopyContent content={String(appointment.healthie_id)}>
                      <span>{appointment.healthie_id}</span>
                    </CopyContent>
                  ) : (
                    'Not Available'
                  )}
                </DataRow>

                {/* Join link shown here only when focused on a specific person */}
                {canGenerateJoinLink && personId && (
                  <DataRow label="Member Zoom Link">
                    {!joinLink ? (
                      <Button
                        onClick={() => attendanceId && fetchJoinLink(attendanceId)}
                        disabled={loadingJoinLink || !attendanceId}
                        loading={loadingJoinLink}>
                        Get Link
                      </Button>
                    ) : (
                      <span className="flex items-center gap-2">
                        {copiedJoinLink && (
                          <span className="text-green-600 text-sm font-medium">
                            ✓ Copied to clipboard!
                          </span>
                        )}
                        <a
                          className="text-blue-600 underline text-sm"
                          href={joinLink}
                          target="_blank"
                          rel="noreferrer">
                          Open
                        </a>
                      </span>
                    )}
                  </DataRow>
                )}

                <DataRow label="Direct Zoom URL">
                  {appointment.zoom_join_url ? (
                    <CopyContent content={appointment.zoom_join_url}>
                      <a
                        className="text-blue-600 underline"
                        href={appointment.zoom_join_url}
                        target="_blank"
                        rel="noreferrer">
                        {appointment.zoom_join_url}
                      </a>
                    </CopyContent>
                  ) : (
                    'Not Available'
                  )}
                </DataRow>
              </DataList>

              <Debug object={appointment} />
            </Accordion>
          </section>
        </div>
      ) : (
        <p>Appointment not found</p>
      )}

      {renderDrugScreens}

      {attendanceResults.length > 0 && (
        <AppointmentSummary
          attendances={attendanceResults}
          meetingStatus={appointment?.meeting_status}
          activeCard={activeSummaryCard}
          onActiveCardChange={setActiveSummaryCard}
          onFilterChange={setAttendanceFilterIds}
        />
      )}

      {renderAttendances}

      <BulkMessageModal
        open={isBulkModalOpen}
        onClose={() => setIsBulkModalOpen(false)}
        selectedPeople={filteredAttendances.map((a) => a.person!)}
        onSuccess={() => setIsBulkModalOpen(false)}
      />

      {videoSessions && (
        <section className="my-4">
          <Heading className="mb-1" value={3} text="Video Sessions" />
          <Card>
            <VideoSessionsChart videoSessions={videoSessions} />
          </Card>
        </section>
      )}
    </Container>
  );
};

export default AppointmentView;
